/**
 * ProductImageGallery — carousel + thumbnail strip for the product page
 */
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "@shopify/hydrogen-react";
import { ProductImageLightbox } from "@modules/shop/components/SingleProduct/ProductImageLightbox";
import {
  Carousel,
  
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "ui/components/ui/carousel";
import { useProductImageLightbox } from "./useProductImageLightbox";
import type {CarouselApi} from "ui/components/ui/carousel";

interface ProductImage {
  id?: string | null;
  url: string;
  altText?: string | null;
  width?: number | null;
  height?: number | null;
}

interface ProductImageGalleryProps {
  images: Array<ProductImage>;
  productTitle: string;
  productHandle: string;
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export function ProductImageGallery({
  images,
  productTitle,
  productHandle,
  selectedIndex,
  onSelect,
}: Readonly<ProductImageGalleryProps>) {
  const { t } = useTranslation();
  const apiRef = useRef<CarouselApi>(undefined);
  const lightbox = useProductImageLightbox({ images, selectedIndex, onSelect });

  const onApiReady = useCallback(
    (api: CarouselApi) => {
      if (!api) return;
      apiRef.current = api;

      api.on("select", () => {
        onSelect(api.selectedScrollSnap());
      });
    },
    [onSelect]
  );

  const handleThumbnailClick = useCallback(
    (index: number) => {
      onSelect(index);
      apiRef.current?.scrollTo(index);
    },
    [onSelect]
  );

  return (
    <div>
      {images.length > 0 ? (
        <Carousel
          opts={{ loop: true, startIndex: selectedIndex }}
          setApi={onApiReady}
          className="w-full"
          style={{ viewTransitionName: `product-image-${productHandle}` }}
        >
          <CarouselContent>
            {images.map((image, i) => (
              <CarouselItem key={image.id ?? i}>
                <button
                  type="button"
                  onClick={() => lightbox.open(i)}
                  className="aspect-square w-full cursor-zoom-in overflow-hidden rounded-3xl bg-white"
                  aria-label={t("Common.viewImage", "Open image in fullscreen")}
                >
                  <Image
                    src={image.url}
                    alt={image.altText ?? `${productTitle} ${i + 1}`}
                    className="h-full w-full object-contain p-6 max-mobile:p-3"
                    loading={i === 0 ? "eager" : "lazy"}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                  />
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <>
              <CarouselPrevious
                className="left-4 size-10 border-0 bg-white shadow-[0_6px_16px_-8px_rgb(0_0_0/0.35)] hover:bg-background-6"
                aria-label={t("Common.previousSlide", "Previous image")}
              />
              <CarouselNext
                className="right-4 size-10 border-0 bg-white shadow-[0_6px_16px_-8px_rgb(0_0_0/0.35)] hover:bg-background-6"
                aria-label={t("Common.nextSlide", "Next image")}
              />
            </>
          )}
        </Carousel>
      ) : (
        <div className="flex aspect-square items-center justify-center rounded-3xl bg-white">
          <span className="text-text-4">{t("Common.noImage")}</span>
        </div>
      )}

      {images.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto p-0.5">
          {images.map((image, i) => (
            <button
              key={image.id ?? i}
              type="button"
              onClick={() => handleThumbnailClick(i)}
              aria-current={selectedIndex === i || undefined}
              className={`aspect-square w-20 shrink-0 overflow-hidden rounded-2xl bg-white transition-shadow ${
                selectedIndex === i
                  ? "ring-2 ring-primary"
                  : "ring-1 ring-black/[0.06] hover:ring-black/20"
              }`}
            >
              <Image
                src={image.url}
                alt={image.altText ?? `${productTitle} ${i + 1}`}
                className="h-full w-full object-contain p-1.5"
                loading="lazy"
                sizes="80px"
              />
            </button>
          ))}
        </div>
      )}

      <ProductImageLightbox
        images={images}
        selectedIndex={selectedIndex}
        productTitle={productTitle}
        isOpen={lightbox.isOpen}
        zoomLevel={lightbox.zoomLevel}
        pan={lightbox.pan}
        isPanning={lightbox.isPanning}
        onOpenChange={(open: boolean) =>
          open ? lightbox.open(selectedIndex) : lightbox.close()
        }
        onPrevious={lightbox.goToPrevious}
        onNext={lightbox.goToNext}
        onResetTransform={lightbox.resetTransform}
        onZoomIn={lightbox.zoomIn}
        onZoomOut={lightbox.zoomOut}
        onWheel={lightbox.onWheel}
        onMouseDown={lightbox.onMouseDown}
        onMouseMove={lightbox.onMouseMove}
        onMouseUp={lightbox.onMouseUp}
        onMouseLeave={lightbox.onMouseLeave}
        onTouchStart={lightbox.onTouchStart}
        onTouchMove={lightbox.onTouchMove}
        onTouchEnd={lightbox.onTouchEnd}
        onImageClick={lightbox.onImageClick}
      />
    </div>
  );
}
